import { collection, doc, getDoc, getDocs, limit, orderBy, query, runTransaction, serverTimestamp } from "firebase/firestore";
import { getFirebaseDb, isFirebaseConfigured } from "./firebaseApp";

const REFRESH_INTERVAL_MS = 12 * 60 * 1000;

const SEED_ENTRIES = {
	campaign: [
		{ username: "NovaDrift104", score: 980, mapIdx: 2, wave: 10 },
		{ username: "IonLancer612", score: 910, mapIdx: 2, wave: 9 },
		{ username: "SolarComet214", score: 820, mapIdx: 1, wave: 8 },
		{ username: "VantaCircuit330", score: 790, mapIdx: 1, wave: 8 },
		{ username: "NeonBeacon338", score: 760, mapIdx: 1, wave: 7 },
		{ username: "EchoVector591", score: 725, mapIdx: 1, wave: 7 },
		{ username: "SwiftFalcon482", score: 690, mapIdx: 1, wave: 6 },
		{ username: "TitanSignal266", score: 655, mapIdx: 1, wave: 6 },
		{ username: "GoldenRocket517", score: 610, mapIdx: 0, wave: 18 },
		{ username: "CinderRelay845", score: 570, mapIdx: 0, wave: 17 },
		{ username: "RoguePylon173", score: 520, mapIdx: 0, wave: 16 },
	],
	survival: [
		{ username: "ShadowQuasar229", score: 960, mapIdx: 0, wave: 16 },
		{ username: "RadiantHelix471", score: 840, mapIdx: 0, wave: 14 },
		{ username: "IronCitadel608", score: 760, mapIdx: 0, wave: 13 },
		{ username: "TurboVortex715", score: 620, mapIdx: 0, wave: 11 },
		{ username: "PhantomArray381", score: 560, mapIdx: 2, wave: 10 },
		{ username: "CobaltRift294", score: 505, mapIdx: 1, wave: 9 },
		{ username: "SableMeteor422", score: 470, mapIdx: 3, wave: 9 },
		{ username: "AtlasPulse553", score: 430, mapIdx: 4, wave: 8 },
		{ username: "GammaSpire188", score: 390, mapIdx: 2, wave: 8 },
		{ username: "SilverOrbit907", score: 340, mapIdx: 1, wave: 7 },
	],
};

const requireDb = () => {
	const db = getFirebaseDb();
	if (!isFirebaseConfigured || !db) {
		throw new Error("Firestore is not configured. Add Firebase environment variables first.");
	}
	return db;
};

const getDayKey = (date = new Date()) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const leaderboardCollection = (db, dayKey, mode) => collection(db, "dailyLeaderboards", dayKey, "modes", mode, "entries");
const leaderboardDoc = (db, dayKey, mode, uid) => doc(db, "dailyLeaderboards", dayKey, "modes", mode, "entries", uid);

const formatEntries = (entries, mode) => {
	const seeds = (SEED_ENTRIES[mode] || []).map((entry, index) => ({
		id: `seed-${mode}-${index}`,
		...entry,
		seeded: true,
	}));

	const combined = [...entries];
	for (const seed of seeds) {
		if (combined.length >= 10) break;
		combined.push(seed);
	}

	return combined
		.sort((left, right) => right.score - left.score)
		.slice(0, 10)
		.map((entry, index) => ({ ...entry, rank: index + 1 }));
};

export const getLeaderboardRefreshInterval = () => REFRESH_INTERVAL_MS;

export const loadTopLeaderboardEntries = async (mode = "campaign", dayKey = getDayKey()) => {
	if (!isFirebaseConfigured) return formatEntries([], mode);

	const db = requireDb();
	const leaderboardQuery = query(
		leaderboardCollection(db, dayKey, mode),
		orderBy("score", "desc"),
		limit(10),
	);
	const snapshot = await getDocs(leaderboardQuery);
	const entries = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
	return formatEntries(entries, mode);
};

export const submitLeaderboardScore = async ({
	user,
	profile,
	mode = "campaign",
	score,
	mapIdx = 0,
	wave = 0,
	dayKey = getDayKey(),
}) => {
	if (!isFirebaseConfigured || !user?.uid || !profile?.username || !Number.isFinite(score) || score <= 0) return false;

	const db = requireDb();
	const entryRef = leaderboardDoc(db, dayKey, mode, user.uid);

	await runTransaction(db, async (transaction) => {
		const existing = await transaction.get(entryRef);
		const previousScore = existing.exists() ? existing.data()?.score || 0 : 0;
		if (previousScore >= score) return;

		transaction.set(entryRef, {
			uid: user.uid,
			username: profile.username,
			score,
			mapIdx,
			wave,
			mode,
			updatedAt: serverTimestamp(),
		}, { merge: true });
	});

	return true;
};

export const getCurrentLeaderboardDayKey = getDayKey;
